import { PaginatedDataDTO } from '@bottega52/commons-pagination';
import { createStyles, WithStyles, withStyles } from '@material-ui/core/styles';
import 'moment/locale/it';
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import ReduxLanguage from '../components/ReduxLanguage';
import * as SettingsActions from '../redux/settings/settings.actions';
import { IState } from '../redux/store';
import IClusterInDTO from '../repository/jago/model/input/IClusterInDTO';
import { IParamConfigurationInDTO } from '../repository/jago/model/input/IParamConfigurationInDTO';
import AbilityProvider from '../services/PermissionService/AbilityProvider';
import { Roles } from '../services/PermissionService/PermissionConstants';
import { SALES_APP_VERSION_PARAM_KEY } from '../utils/AppConstants';
import {
  buildSalesAppsIframeUrl,
  getParamConfigValue,
  isSalesAppLaunchpadBaseUrl,
  readVashoAuthToken,
  resolveSalesAppBaseUrl,
} from '../utils/ParamConfigUtils';

const styles = createStyles({
  container: {
    backgroundColor: 'white',
    width: '100%',
    height: '100%',
  },
  launchpadMessage: {
    padding: 24,
    maxWidth: 560,
  },
});

type IReduxProps = ConnectedProps<typeof connector>;

export interface IIseoSalesAppsPageProps extends WithStyles<typeof styles> {}

export type ComponentProps = IIseoSalesAppsPageProps & IReduxProps;

export interface IIseoSalesAppsPageState {
  publicConfigLoaded: boolean;
  paramPublicConfigs: IParamConfigurationInDTO[];
}

class IseoSalesAppsPage extends React.Component<ComponentProps, IIseoSalesAppsPageState> {
  constructor(props: ComponentProps) {
    super(props);
    this.state = {
      publicConfigLoaded: false,
      paramPublicConfigs: [],
    };
  }

  buildIframeUrl(paramPublicConfigs: IParamConfigurationInDTO[]) {
    const { language, clusters, userEmail } = this.props;
    const clustersData = clusters as PaginatedDataDTO<IClusterInDTO>;
    const clusterNames = clustersData?.content?.map(c => c.name).join(',') || '';
    const isSofiaAdmin = AbilityProvider.getAbilityHelper().hasRoles([Roles.ROLE_VASHO_SOFIA_ADMIN]);
    const rawParamValue = getParamConfigValue(paramPublicConfigs, SALES_APP_VERSION_PARAM_KEY, '');
    const salesAppsBaseUrl = resolveSalesAppBaseUrl(rawParamValue);
    const vashoToken = readVashoAuthToken();
    const iframeUrl = buildSalesAppsIframeUrl(rawParamValue, {
      language: language || 'en',
      clusterNames,
      userEmail: userEmail || '',
      featureLevel: isSofiaAdmin ? 'full' : 'standard',
      vashoToken,
    });
    return {
      iframeUrl,
      vashoToken,
      isLaunchpad: isSalesAppLaunchpadBaseUrl(salesAppsBaseUrl),
    };
  }

  async componentDidMount() {
    const { dispatch } = this.props;
    const paramPublicConfigs = await dispatch<any>(SettingsActions.fetchParamPublicConfiguration());
    this.setState({
      publicConfigLoaded: true,
      paramPublicConfigs: paramPublicConfigs || [],
    });
  }

  render() {
    const { classes } = this.props;
    const { publicConfigLoaded, paramPublicConfigs } = this.state;

    if (!publicConfigLoaded) {
      return null;
    }

    const { iframeUrl, vashoToken, isLaunchpad } = this.buildIframeUrl(paramPublicConfigs);

    if (!iframeUrl) {
      return (
        <div className={classes.launchpadMessage}>
          <p><ReduxLanguage languageKey="iseoSalesApps.missingBaseUrl" /></p>
        </div>
      );
    }

    if (isLaunchpad && !vashoToken) {
      return (
        <div className={classes.launchpadMessage}>
          <p><ReduxLanguage languageKey="iseoSalesApps.launchpadMissingToken" /></p>
        </div>
      );
    }

    return (
      <div style={{ maxHeight: 'calc(100vh - 64px)', minHeight: 'calc(100vh - 64px)' }}>
        <div className={classes.container}>
          <iframe
            key={iframeUrl}
            src={iframeUrl}
            title="ISEO Sales App"
            referrerPolicy="no-referrer-when-downgrade"
            allow="storage-access-by-user-activation *; fullscreen *"
            style={{
              border: 0,
              width: 'calc(100%)',
              maxHeight: 'calc(100vh - 64px)',
              minHeight: 'calc(100vh - 64px)',
            }}
          />
        </div>
      </div>
    );
  }
}

function mapStateToProps(state: IState) {
  return {
    language: state.settings.language,
    clusters: state.credits.clusters.data,
    userEmail: state.user.userData?.user_name || '',
    paramPublicConfigs: state.settings.paramPublicConfigs.data,
  };
}

const connector = connect(mapStateToProps);

export default connector(withStyles(styles)(IseoSalesAppsPage as any));
